import { useState } from 'react'
import { isEmpty } from 'lodash'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import Modal from 'components/Modal'
import Info from 'components/PosAccounts/Form/Info'
import AccountType from 'components/PosAccounts/Form/AccountType'
import AccountStatus from 'components/PosAccounts/Form/AccountStatus'
import ConvertToOrgAttendant from 'components/PosAccounts/Form/ConvertToOrgAttendant'
import PosAccount from 'components/PosAccounts/Form/Drawers/PosAccount'

import {
  getAttendantByUuid,
  getIsLoadingAttendants,
} from 'store/Attendants/selectors'
import {
  createAttendantWithRole,
  updateAttendant,
  updateAttendantWithRole,
} from 'store/Attendants/actions'
import { getOrgName } from 'store/AppState/selector'
import { handleAttendantFormSubmit } from '../utils/handleAttendantFormSubmit'

const PosAccountsDetailes = ({ uuid }) => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)

  const history = useHistory()
  const dispatch = useDispatch()

  const currentAttendant = useSelector((state) =>
    getAttendantByUuid(state, uuid)
  )
  const orgName = useSelector(getOrgName)
  const isAttendantsLoading = useSelector(getIsLoadingAttendants)

  // when deactivatedAt = null then account is activated, when deactivatedAt = timestamp then account is deactivated
  const isDeactivatedAccount = !isEmpty(
    currentAttendant?.deactivatedAt
  )

  // when account is assigned we suposed to display "convert message", disable all inputs and submit button
  const isAssignedAccount = !isEmpty(currentAttendant?.venueUuid)
  const isNewPosAccount = uuid === 'new'
  const isAssignedAndNotNewAccount =
    isAssignedAccount && !isNewPosAccount
  const isActiveAndNotNewAccount =
    !isDeactivatedAccount && !isNewPosAccount

  const modalTitle = isNewPosAccount
    ? 'Add POS Account'
    : 'Edit POS Account'

  const initialValues = {
    name: currentAttendant?.name ?? '',
    phone: currentAttendant?.phone ?? '',
    email: currentAttendant?.email ?? '',
    pinNumber: currentAttendant?.pinNumber ?? '',
    employeeId: currentAttendant?.employeeId ?? '',
    role:
      currentAttendant?.employeeRole === 'manager'
        ? 'manager'
        : 'cart_attendant',
    roleUuid: currentAttendant?.roleUuid,
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    pinNumber: Yup.string()
      .required('Required')
      .test(
        'pinNumberLength',
        'PIN Code must be between 5 and 6 digits',
        function (value) {
          if (!value) return false

          const isTouchedInitialValue = initialValues.pinNumber !== value
          const isValidEditedValue = value.length >= 5 && value.length <= 6

          // pinNumber is valid when:
          // 1. is not changed, then we accept less than 5 numbers
          // 2. updated number has between 5 and 6 numbers
          if (!isTouchedInitialValue) return true
          if (isValidEditedValue) return true

          return false
        }
      ),
  })

  const handleAccountStatusClick = (action) => {
    const currentDate = String(new Date())

    const updatePosAccountValues = {
      uuid: currentAttendant?.uuid,
      employeeId: currentAttendant?.employeeId,
      deactivatedAt: action === 'deactivate' ? currentDate : null,
    }

    dispatch(updateAttendant(updatePosAccountValues)).then(() => {
      history.push({ state: { posAccountUuid: null } })
    })
  }

  const settings = [
    {
      Component: AccountStatus,
      isVisible: !isNewPosAccount,
      isDeactivatedAccount: isDeactivatedAccount,
      deactivatedAt: currentAttendant?.deactivatedAt,
      handleReactivatePosAccount: handleAccountStatusClick,
    },
    {
      Component: ConvertToOrgAttendant,
      isVisible: isAssignedAndNotNewAccount,
      currentAttendant: currentAttendant,
    },
    {
      Component: Info,
      showValidationErrors: showValidationErrors,
      disabled: isAssignedAndNotNewAccount,
    },
    {
      Component: AccountType,
      disabled: isAssignedAndNotNewAccount,
    },
  ]

  const onFormKeyDown = (event) => {
    if ((event.charCode || event.keyCode) === 13) {
      event.preventDefault()
    }
  }

  const onClose = () =>
    history.push({ state: { posAccountUuid: null } })

  const handleSubmit = (values) => {
    const onCreateAttendant = (createAttendantWithRoleData) =>
      dispatch(
        createAttendantWithRole(createAttendantWithRoleData)
      ).then(() => history.push({ state: { posAccountUuid: null } }))

    const onUpdateAttendant = (updateAttendantWithRoleData) =>
      dispatch(
        updateAttendantWithRole(updateAttendantWithRoleData)
      ).then(() => history.push({ state: { posAccountUuid: null } }))

    return handleAttendantFormSubmit(
      values,
      initialValues,
      currentAttendant,
      isNewPosAccount,
      orgName,
      onCreateAttendant,
      onUpdateAttendant
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {() => (
        <Modal
          title={modalTitle}
          onClose={onClose}
          className="-narrow"
          isLoading={isAttendantsLoading}
          onRequestClose={onClose}
        >
          <div className="modal-body">
            <Form
              onKeyDown={onFormKeyDown}
              className="pos-accounts-settings"
            >
              <PosAccount
                settings={settings}
                isActiveAndNotNewAccount={isActiveAndNotNewAccount}
                isDeactivatedAccount={isDeactivatedAccount}
                handleAccountStatusClick={handleAccountStatusClick}
                showValidationErrors={showValidationErrors}
                setShowValidationErrors={setShowValidationErrors}
              />
            </Form>
          </div>
        </Modal>
      )}
    </Formik>
  )
}

export default PosAccountsDetailes
